import type {
    BlockRegistry,
    PartialBlockRegistry,
} from './types';
import type {
    CardBlockRegistry,
    PanelBlockRegistry,
    HeaderBlockRegistry,
} from './types';
import {
    MediaBlock,
    TitleBlock,
    PriceBlock,
    StockBadgeBlock,
    // HighlightsBlock,
    CartActionsBlock,
    useCartActionsBlocks,
} from './cardBlocks';
import {
    HeroBlock,
    ToolbarBlock,
    PanelHeadingBlock,
    GridBlock,
    PaginationBlock,
    EmptyStateBlock,
} from './panelBlocks';
import {
    BrandBlock,
    NavigationBlock,
    ActionsBlock,
    ConfiguratorBlock,
    HeaderSearchBlock,
} from './headerBlocks';
import { softCommerceBlocks } from '../themes/soft-commerce/blocks';

const defaultCardBlocks: CardBlockRegistry = {
    Media: MediaBlock,
    Title: TitleBlock,
    Price: PriceBlock,
    StockBadge: StockBadgeBlock,
    Highlights: undefined, // HighlightsBlock,
    CartActions: CartActionsBlock,
};

const defaultPanelBlocks: PanelBlockRegistry = {
    Hero: HeroBlock,
    Toolbar: ToolbarBlock,
    PanelHeading: PanelHeadingBlock,
    Grid: GridBlock,
    Pagination: PaginationBlock,
    EmptyState: EmptyStateBlock,
};

const defaultHeaderBlocks: HeaderBlockRegistry = {
    Brand: BrandBlock,
    Navigation: NavigationBlock,
    Actions: ActionsBlock,
    Configurator: ConfiguratorBlock,
    HeaderSearch: HeaderSearchBlock,
};

const DEFAULT_BLOCK_REGISTRY: BlockRegistry = {
    card: defaultCardBlocks,
    panel: defaultPanelBlocks,
    header: defaultHeaderBlocks,
};

const THEME_BLOCK_REGISTRIES: Record<string, PartialBlockRegistry> = {
    'soft-commerce': softCommerceBlocks,
};

let currentRegistry: BlockRegistry = DEFAULT_BLOCK_REGISTRY;

const mergeBlockRegistry = (
    base: BlockRegistry,
    overrides?: PartialBlockRegistry,
): BlockRegistry => ({
    card: { ...base.card, ...(overrides?.card ?? {}) } as CardBlockRegistry,
    panel: { ...base.panel, ...(overrides?.panel ?? {}) } as PanelBlockRegistry,
    header: { ...base.header, ...(overrides?.header ?? {}) } as HeaderBlockRegistry,
});

export const getBlockRegistry = (): BlockRegistry => currentRegistry;

export const getBlockRegistryForDesign = (design?: string | null): BlockRegistry => {
    const themeBlocks = design ? THEME_BLOCK_REGISTRIES[design] : undefined;
    return themeBlocks ? mergeBlockRegistry(currentRegistry, themeBlocks) : currentRegistry;
};

export const registerBlocks = (overrides: PartialBlockRegistry): BlockRegistry => {
    currentRegistry = mergeBlockRegistry(currentRegistry, overrides);
    return currentRegistry;
};

export const createBlockRegistry = (
    overrides?: PartialBlockRegistry,
    base: BlockRegistry = DEFAULT_BLOCK_REGISTRY,
): BlockRegistry => mergeBlockRegistry(base, overrides);

export const resetBlockRegistry = (): BlockRegistry => {
    currentRegistry = DEFAULT_BLOCK_REGISTRY;
    return currentRegistry;
};

export const blockRegistry = DEFAULT_BLOCK_REGISTRY;

export type {
    CardBlockProps,
    PanelBlockProps,
    HeaderBlockProps,
    CardBlockComponent,
    PanelBlockComponent,
    HeaderBlockComponent,
    CardBlockRegistry,
    PanelBlockRegistry,
    HeaderBlockRegistry,
    BlockRegistry,
    PartialBlockRegistry,
    BlockSlotAccessors,
} from './types';

export {
    MediaBlock,
    TitleBlock,
    PriceBlock,
    StockBadgeBlock,
    // HighlightsBlock,
    CartActionsBlock,
    useCartActionsBlocks,
    HeroBlock,
    ToolbarBlock,
    PanelHeadingBlock,
    GridBlock,
    PaginationBlock,
    EmptyStateBlock,
    BrandBlock,
    NavigationBlock,
    ActionsBlock,
    ConfiguratorBlock,
    HeaderSearchBlock,
};

export {
    createSlotRenderer,
    isDeclarativeComponent,
    resolveLayoutProps,
    CARD_REGION,
    PANEL_REGION,
    HEADER_REGION,
} from './renderers';
export type {
    DeclarativeComponent,
    DeclarativeRenderConfig,
    SlotRenderer,
    GenericBlockProps,
} from './renderers';
